//
//  ZM - Reviewed by Claude on 03/19/2026.
//
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { VehicleRec, DecodeRec, VehicleStockPhoto, PhotoList } from 'src/models';
import { AxiosError } from 'axios';
import * as acBase from 'src/store/states/GenericActionCreator';
import { ViewMode } from 'src/enums';
import { getProviderId, sendSdEvent } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';
import { AppThunk } from 'src/types';

/** Interfaces */
export interface IVehiclesState {
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    decodedData: DecodeRec;
    errMsg?: string;
    vehiclePhoto: VehicleStockPhoto;
    vehPhotoList: PhotoList;
    addVinError: string;
    vehiclesBuilt: boolean;
}

export interface NextAppt {
    index: string;
    nextAppt: string;
}

/** Initial State */
const initialState: IVehiclesState = {
    vehicles: [],
    selectedVehicle: '',
    decodedData: {
        make: '',
        makeCode: '',
        model: '',
        year: '',
        style: '',
        color: '',
    },
    vehiclePhoto: {
        PhotoAngle: null,
        ThumbnailUrl: null,
        Url: null,
        id: '',
    },
    vehPhotoList: [],
    addVinError: '',
    vehiclesBuilt: false,
};

export const VehiclesSlice = createSlice({
    name: 'Vehicles',
    initialState,
    reducers: {
        receiveVehicles: (state, action: PayloadAction<VehicleRec[]>) => {
            state.vehicles = action.payload;
        },
        selectVehicle: (state, action: PayloadAction<string>) => {
            state.selectedVehicle = action.payload;
        },
        changeMileage: (state, action: PayloadAction<VehicleRec[]>) => {
            state.vehicles = action.payload;
        },
        addVehicle: (state, action: PayloadAction<VehicleRec>) => {
            state.vehicles.push(action.payload);
        },
        decodeVin: (state, action: PayloadAction<DecodeRec>) => {
            state.decodedData = action.payload;
        },
        clearDecode: (state) => {
            state.decodedData = {
                make: '',
                makeCode: '',
                model: '',
                year: '',
                style: '',
                color: '',
                decodeErr: '',
            };
        },
        createVehicle: (state, action: PayloadAction<string>) => {
            state.errMsg = action.payload;
        },
        clearError: (state) => {
            state.errMsg = undefined;
        },
        setVehNextAppt: (state, action: PayloadAction<VehicleRec[]>) => {
            state.vehicles = action.payload;
        },
        clearVehicles: (state) => {
            state.vehicles = initialState.vehicles;
            state.decodedData = initialState.decodedData;
            state.errMsg = initialState.errMsg;
            state.selectedVehicle = initialState.selectedVehicle;
            state.vehPhotoList = initialState.vehPhotoList;
        },
        setAddVinError: (state, action: PayloadAction<string>) => {
            state.addVinError = action.payload;
        },
        setVehiclesBuilt: (state, action: PayloadAction<boolean>) => {
            state.vehiclesBuilt = action.payload;
        },
    },
});

export const {
    receiveVehicles,
    selectVehicle,
    changeMileage,
    addVehicle,
    decodeVin,
    clearDecode,
    createVehicle,
    clearError,
    setVehNextAppt,
    clearVehicles,
    setAddVinError,
    setVehiclesBuilt,
} = VehiclesSlice.actions;

export default VehiclesSlice.reducer;

// ServiceDocs and ViewServiceHist are the Order Service Document view modes used by the service
// department directly — these call the OSD (Order Service Document) program instead of SVP.
function getCallingProgram(viewMode: ViewMode): string {
    return (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) ? 'OSD' : 'SVP';
}

export function GetVehicles(): AppThunk {
    return (dispatch, getState) => {
        const vehicleTable: VehicleRec[] = [];

        // get vehicles from tomer state
        const custState = getState().Customer.Customer;

        if (custState && custState.vehicleList) {
            vehicleTable.push(...custState.vehicleList);
        }

        // If a vehicle exists in the appointment state, find that vehicle in the new vehicle list
        // and select it
        const apptVehicle = getState().Appointment.ApptVeh;
        if (apptVehicle) {
            if (apptVehicle.vin) {
                // match the last 8 characters of the vin
                const vehicleIndex = vehicleTable.findIndex(veh => veh?.vin?.slice(-8) === apptVehicle?.vin?.slice(-8));
                if (vehicleIndex >= 0) {
                    dispatch(selectVehicle(vehicleTable[vehicleIndex].id));
                }
            } else {
                const vehicleIndex = vehicleTable.findIndex(veh => veh.make === apptVehicle.make &&
                    veh.model === apptVehicle.model && veh.year === apptVehicle.vehYr);
                if (vehicleIndex >= 0) {
                    dispatch(selectVehicle(vehicleTable[vehicleIndex].id));
                }
            }
        }

        dispatch(receiveVehicles(vehicleTable));
        dispatch(setVehiclesBuilt(true));
    };
}

export function SelectVehicle(id: string): AppThunk {
    return (dispatch) => {
        dispatch(selectVehicle(id));
    };
}

export function ClearVehicles(): AppThunk {
    return (dispatch) => {
        dispatch(clearVehicles());
        dispatch(setVehiclesBuilt(false));
    };
}

// Make a shallow copy of the vehicles array, then find the index at which it needs to be changed.
// Afterwards, create a shallow copy of the vehicle we're changing and change the mileage.
// Then insert the vehicle back into the vehicle array and pass it as the payload.
export function ChangeMileage(id: string, mileage: number): AppThunk {
    return (dispatch, getState) => {
        const vehicles = [...getState().Vehicles.vehicles];
        const vehicleIndex = vehicles.findIndex(veh => veh.id === id);
        const vehicleToChange = { ...vehicles[vehicleIndex] };
        vehicleToChange.prevMileage = mileage;
        vehicles[vehicleIndex] = vehicleToChange;
        dispatch(changeMileage(vehicles));
    };
}

export function AddVehicle(newVehicle: VehicleRec): AppThunk<Promise<void>> {
    return async (dispatch, getState) => {
        const vehicleTable = getState().Vehicles.vehicles;
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const logId = getState().Specs.specs?.logId;
        const tempVehicle = { ...newVehicle };
        const existingVeh = vehicleTable.find(veh => veh?.vin?.slice(-8) === tempVehicle?.vin?.slice(-8));
        // If the VIN already exists in the list, select it and set error code '0000' to signal
        // the UI that the vehicle was found rather than added.
        if (existingVeh && existingVeh?.id) {
            dispatch(selectVehicle(existingVeh.id));
            dispatch(setAddVinError('0000'));
        }
        if (!existingVeh) {
            const custId = getState().Customer.Customer.ascCustId;
            const multipleCustomers = getState().Customer.multipleCustomers;
            // For single customers with an ascCustId, the API expects a 0-based index as the custUserID.
            // For multiple-customer scenarios the array length (1-based) is used instead.
            tempVehicle.id = ((custId && !multipleCustomers) ? vehicleTable.length - 1 : vehicleTable.length).toString();
            const callingProgram = getCallingProgram(getState().Framework.viewMode);
            const reqData = {
                vehVin: tempVehicle.vin,
                custUserID: tempVehicle.id,
                logId: logId,
                callingProgram: callingProgram,
            };
            // Reset id to the actual array index used when storing the vehicle in state.
            tempVehicle.id = vehicleTable.length.toString();
            const response = await acBase.eraWebServiceCall<DecodeRec | AxiosError>(
                'SVPController', 'AddVinLog', reqData, store, branch);
            dispatch(addVehicle(tempVehicle));
            dispatch(selectVehicle(tempVehicle.id));
            dispatch(setAddVinError(response.addVinError));
        }
    };
}

export function DecodeVin(newVin: string, store: string, branch: string, checkOwned: string = '',
    lastName?: string, phoneNumber?: string, email?: string, nameId?: string): AppThunk<Promise<void>> {
    return async (dispatch, getState) => {
        const decodedData: DecodeRec =
        {
            make: '',
            makeCode: '',
            model: '',
            year: '',
            style: '',
            color: '',
            decodeErr: '',
        };
        const logId = getState().Framework.logId;
        const callingProgram = getCallingProgram(getState().Framework.viewMode);
        const reqData = {
            keyVin: newVin,
            checkOwned: checkOwned,
            lastName: lastName,
            phoneNumber: phoneNumber,
            email: email,
            custID: nameId,
            logId: logId,
            callingProgram: callingProgram,
        };
        let response = await acBase.eraWebServiceCall<DecodeRec | AxiosError>('SVPController', 'DecodeVin', reqData, store, branch);
        // On a network/server error, fall back to an empty decode record so the UI
        // receives a consistent shape rather than an AxiosError object.
        if (response && (response as AxiosError).isAxiosError) {
            response = decodedData as DecodeRec;
        }
        dispatch(decodeVin(response));
    };
}

export function ClearDecodeData(): AppThunk {
    return (dispatch) => {
        dispatch(clearDecode());
    };
}

// This uploads a new vehicle into the record through PICK
export function CreateVehicle(
    newVehicle: VehicleRec, custID: string, makeCode: string, store: string, branch: string, previewFlag: string, isOEM?: string): AppThunk<Promise<void>> {
    return async (dispatch, getState) => {
        const sessionId = getState().Framework.sessionId;
        const specs = getState().Specs.specs;
        const toyotaMCLaunched = getState().Framework.toyotaMCLaunched;
        const toyotaTDMCLaunched = getState().Framework.toyotaTDMCLaunched;
        const lexusMCLaunched = getState().Framework.lexusMCLaunched;
        const lexusTDMCLaunched = getState().Framework.lexusTDMCLaunched;

        const logId = getState().Specs.specs?.logId;
        // The server expects a 2-digit year — trim the century if a full 4-digit year was provided.
        let vehicleYear = newVehicle.year;
        if (vehicleYear.length === 4) {
            vehicleYear = vehicleYear.slice(2, 4);
        }
        const callingProgram = getCallingProgram(getState().Framework.viewMode);
        const reqData = {
            keyVin: newVehicle.vin,
            custID: custID,
            make: newVehicle.make,
            makeCode: makeCode,
            model: newVehicle.model,
            year: vehicleYear,
            color: newVehicle.color,
            previewFlag: previewFlag,
            logId: logId,
            callingProgram: callingProgram,
            isOEM: isOEM,
        };
        const response = await acBase.eraWebServiceCall<VehicleRec | AxiosError>(
            'SVPController', 'CreateVehicle', reqData, store, branch);

        // Extract the error message from either an Axios network error or a decode error returned
        // by the server — Axios message takes priority, falling back to decodeErr.
        if (response && ((response as AxiosError).isAxiosError || response.decodeErr)) {
            response.errMsg = (response as AxiosError).message;
            if (!response.errMsg) {
                response.errMsg = response.decodeErr;
            }
        }

        const isSDAnalyticsEnabled =
            specs?.toyotaTrackingFlag === 'Y' ||
            specs?.lexusTrackingFlag === 'Y' ||
            specs?.mbzTrackingFlag === 'Y' ||
            specs?.mazdaCATrackingFlag === 'Y' ||
            specs?.vwTrackingFlag === 'Y' ||
            specs?.jaguarTrackingFlag === 'Y' ||
            specs?.landRoverTrackingFlag === 'Y';

        if (isSDAnalyticsEnabled) {
            sendSdEvent('scheduleServiceVehicleStart', {
                scheduleServiceSessionId: sessionId,
                scheduleServiceProvider: getProviderId(specs, toyotaMCLaunched, toyotaTDMCLaunched, lexusMCLaunched,
                    lexusTDMCLaunched),
            });
        }

        dispatch(createVehicle(response ? response.errMsg ?? '' : ''));
    };
}

export function VehiclesClearError(): AppThunk {
    return (dispatch) => {
        dispatch(clearError());
    };
}

export function SetVehNextAppt(id: string, apptID: string): AppThunk {
    return (dispatch, getState) => {
        const vehicles = [...getState().Vehicles.vehicles];
        const vehicleIndex = vehicles.findIndex(veh => veh.id === id);
        const vehicleToChange = { ...vehicles[vehicleIndex] };
        vehicleToChange.nextAppt = apptID;
        vehicles[vehicleIndex] = vehicleToChange;
        dispatch(setVehNextAppt(vehicles));
    };
}

export function ClearAddVinError(): AppThunk {
    return (dispatch) => {
        dispatch(setAddVinError(''));
    };
}